/**
 * EcuMatchModal — drop a stack of ECU logs and let the server work out which
 * session each one belongs to.
 *
 * The analysis-page wizard (EcuMergeModal) assumes you already know the
 * session. Users generally don't: an ECU holds many logs named by export time,
 * with no recording time in them at all, so pairing them with a track day's
 * sessions is guesswork. This screen does the pairing by signal correlation and
 * asks you to confirm it.
 *
 * Step 1: pick logs (+ optionally narrow the search by car / date).
 * Step 2: one row per log, each with its best-match session preselected, the
 *         measured confidence, and a dropdown of the runners-up. "Review"
 *         opens the existing merge wizard for that one pair rather than
 *         duplicating its overlay and mapping table here.
 * Step 3: merge the confirmed pairs, reporting per-row status.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { ecuMatch, ecuPreview, ecuCommit } from '../../services/api';
import EcuMergeModal from '../Analysis/EcuMergeModal';
import styles from './EcuMatchModal.module.css';

const MAX_FILES = 10;

// Extensions the match endpoint knows how to open. Vendor archives are
// identified by content server-side; this is just to keep obvious mistakes
// (a video, a PDF) out of a slow request.
const ACCEPTED = [
  '.csv', '.txt', '.tsv', '.zip', '.maxxecu-zip-log', '.maxxecu-log', '.maxxlog', '.log',
];

export function isEcuLogFile(file) {
  const n = (file?.name || '').toLowerCase();
  return ACCEPTED.some(ext => n.endsWith(ext));
}

const VERDICT_UI = {
  confident: { label: 'Confident', color: '#3ddc84' },
  likely: { label: 'Likely', color: '#f5c842' },
  uncertain: { label: 'Uncertain', color: '#ff9b6b' },
  no_candidates: { label: 'No match', color: '#ff6b6b' },
  error: { label: 'Unreadable', color: '#ff6b6b' },
};

function confColor(c) {
  if (c == null) return '#888';
  if (c >= 0.85) return '#3ddc84';
  if (c >= 0.7) return '#f5c842';
  return '#ff6b6b';
}

function fmtDuration(s) {
  if (!s && s !== 0) return '—';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.round(s - m * 60)).padStart(2, '0')}`;
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function candidateLabel(c) {
  const bits = [fmtDate(c.session_date), c.track, c.label].filter(Boolean);
  // The label often *is* the start time, which would then repeat the date.
  const seen = new Set();
  const uniq = bits.filter(b => (seen.has(b) ? false : seen.add(b)));
  return `${uniq.join(' · ')} — ${fmtDuration(c.duration)}, ${c.lap_count} laps`;
}

export default function EcuMatchModal({ initialFiles, onClose, onMerged }) {
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState(() => Array.from(initialFiles || []).filter(isEcuLogFile));
  const [vehicle, setVehicle] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  // filename -> session_id ('' means skip this log)
  const [choice, setChoice] = useState({});
  // filename -> { state: 'pending'|'working'|'done'|'error', msg }
  const [status, setStatus] = useState({});
  const [reviewing, setReviewing] = useState(null);
  const inputRef = useRef(null);

  const addFiles = useCallback((incoming) => {
    const usable = Array.from(incoming || []).filter(isEcuLogFile);
    if (!usable.length) {
      setError('Those files don’t look like ECU logs (.csv, .maxxlog, or .MaxxECU-Zip-log).');
      return;
    }
    setError(null);
    setFiles(prev => {
      const byName = new Map(prev.map(f => [f.name, f]));
      usable.forEach(f => byName.set(f.name, f));
      return Array.from(byName.values()).slice(0, MAX_FILES);
    });
  }, []);

  // ── Step 1 → 2: match ─────────────────────────────────────────────────────
  const runMatch = useCallback(async () => {
    setBusy(true); setError(null); setUploadPct(0);
    try {
      const resp = await ecuMatch(files, { vehicle, dateFrom, dateTo },
                                  pct => setUploadPct(pct));
      setResults(resp.results);
      const init = {};
      resp.results.forEach(r => {
        // Preselect only when the server is confident enough to stand behind
        // it. An uncertain log shows its candidates but commits to nothing.
        const auto = (r.verdict === 'confident' || r.verdict === 'likely')
          && r.candidates[0];
        init[r.filename] = auto ? r.candidates[0].session_id : '';
      });
      setChoice(init);
      setStatus({});
      setStep(2);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false); setUploadPct(null);
    }
  }, [files, vehicle, dateFrom, dateTo]);

  const pending = useMemo(
    () => results.filter(r => choice[r.filename] && status[r.filename]?.state !== 'done'),
    [results, choice, status]);

  // ── Step 2 → 3: merge each confirmed pair ─────────────────────────────────
  const runMerges = useCallback(async () => {
    setBusy(true); setError(null);
    let merged = 0;
    // Sequential on purpose: each merge rewrites a session's telemetry frame,
    // and the server does that work on a thread per request.
    for (const r of pending) {
      const sessionId = choice[r.filename];
      setStatus(s => ({ ...s, [r.filename]: { state: 'working' } }));
      try {
        const pv = await ecuPreview(sessionId, {
          stagingId: r.staging_id, sourceFilename: r.filename,
        });
        const shared = pv.matches.filter(m => m.is_shared).map(m => m.ecu_channel);
        const resp = await ecuCommit(sessionId, {
          staging_id: pv.staging_id,
          source_filename: pv.source_filename,
          offset_s: pv.offset_s,
          sec_per_sample: pv.sec_per_sample,
          sync_channel: pv.sync_channel,
          shared_ecu_channels: shared,
          confidence: pv.confidence,
        });
        merged += 1;
        setStatus(s => ({
          ...s,
          [r.filename]: { state: 'done', msg: `${resp.added_channels.length} channels added` },
        }));
      } catch (e) {
        setStatus(s => ({ ...s, [r.filename]: { state: 'error', msg: String(e.message || e) } }));
      }
    }
    setBusy(false);
    if (merged) onMerged?.(merged);
  }, [pending, choice, onMerged]);

  const reviewRow = useCallback((r) => {
    const sessionId = choice[r.filename];
    if (!sessionId) return;
    setReviewing({ sessionId, filename: r.filename, stagingId: r.staging_id });
  }, [choice]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (reviewing) {
    return (
      <EcuMergeModal
        sessionId={reviewing.sessionId}
        sessionName={reviewing.filename}
        staged={{ stagingId: reviewing.stagingId, sourceFilename: reviewing.filename }}
        onClose={() => setReviewing(null)}
        onMerged={() => {
          setStatus(s => ({
            ...s, [reviewing.filename]: { state: 'done', msg: 'merged' },
          }));
          setReviewing(null);
          onMerged?.(1);
        }}
      />
    );
  }

  return (
    <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            Match ECU Logs
            <span className={styles.subtitle}>
              {step === 1 ? ' — find the right session automatically' : ` — ${results.length} logs`}
            </span>
          </h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
        </div>

        {error && <div className={styles.errorBar}>{error}</div>}

        {step === 1 && (
          <div className={styles.body}>
            <p className={styles.hint}>
              Drop logs straight off your engine computer — MaxxECU{' '}
              <code>.MaxxECU-Zip-log</code> packages, standalone <code>.maxxlog</code>{' '}
              files, or CSV exports. Each one is
              compared against your own sessions and paired with the one it was
              recorded during. You confirm before anything is merged.
            </p>

            {busy ? (
              <div className={styles.dropzone}>
                <span className={styles.dropHint}>
                  {uploadPct != null && uploadPct < 100
                    ? `Uploading… ${uploadPct}%`
                    : 'Comparing against your sessions… this takes a few seconds per log'}
                </span>
                <div className={styles.progressBar}>
                  <div className={`${styles.progressFill} ${uploadPct >= 100 ? styles.progressIndeterminate : ''}`}
                       style={uploadPct != null && uploadPct < 100 ? { width: `${uploadPct}%` } : undefined} />
                </div>
              </div>
            ) : (
              <div
                className={styles.dropzone}
                onClick={() => inputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              >
                <span className={styles.dropHint}>
                  {files.length
                    ? `${files.length} log${files.length > 1 ? 's' : ''} ready`
                    : 'Drop ECU logs here or click to browse'}
                </span>
                <span className={styles.dropSub}>Up to {MAX_FILES} at once</span>
              </div>
            )}
            {/* Outside the dropzone: a programmatic .click() bubbles, and as a
                child it would re-trigger the dropzone's own onClick. */}
            <input
              ref={inputRef} type="file" multiple hidden
              accept=".csv,.txt,.tsv,.zip,.MaxxECU-Zip-log,.MaxxECU-log,.maxxlog"
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
            />

            {files.length > 0 && !busy && (
              <ul className={styles.fileList}>
                {files.map(f => (
                  <li key={f.name} className={styles.fileItem}>
                    <span className={styles.fileName}>{f.name}</span>
                    <span className={styles.fileSize}>{(f.size / 1e6).toFixed(1)} MB</span>
                    <button
                      className={styles.removeBtn}
                      onClick={() => setFiles(prev => prev.filter(x => x.name !== f.name))}
                      aria-label={`Remove ${f.name}`}
                    >×</button>
                  </li>
                ))}
              </ul>
            )}

            <details className={styles.narrow}>
              <summary className={styles.narrowSummary}>Narrow the search (optional)</summary>
              <p className={styles.narrowHint}>
                Only needed if you have a lot of sessions of a similar length —
                the search will tell you when it does.
              </p>
              <div className={styles.narrowRow}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Car</span>
                  <input className={styles.input} value={vehicle} placeholder="any"
                         onChange={e => setVehicle(e.target.value)} />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>From</span>
                  <input className={styles.input} type="date" value={dateFrom}
                         onChange={e => setDateFrom(e.target.value)} />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>To</span>
                  <input className={styles.input} type="date" value={dateTo}
                         onChange={e => setDateTo(e.target.value)} />
                </label>
              </div>
            </details>

            <div className={styles.footer}>
              <button className={styles.secondaryBtn} onClick={onClose} disabled={busy}>Cancel</button>
              <button className={styles.primaryBtn} onClick={runMatch}
                      disabled={busy || !files.length}>
                Find sessions
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className={styles.body}>
            <p className={styles.hint}>
              Each log is paired with the session whose telemetry it matches.
              Change any pairing below, or set it to <em>Skip</em>.
            </p>

            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>ECU log</th>
                    <th>Length</th>
                    <th>Session</th>
                    <th className={styles.thMono}>Match</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {results.map(r => {
                    const st = status[r.filename];
                    const v = VERDICT_UI[r.verdict] || VERDICT_UI.uncertain;
                    const top = r.candidates[0];
                    return (
                      <tr key={r.filename}>
                        <td>
                          <div className={styles.logName} title={r.filename}>{r.filename}</div>
                          <div className={styles.logMeta}>
                            {r.vendor}{r.considered ? ` · ${r.considered} sessions checked` : ''}
                          </div>
                        </td>
                        <td className={styles.tdMono}>{fmtDuration(r.duration_s)}</td>
                        <td>
                          {r.candidates.length ? (
                            <select
                              className={styles.select}
                              value={choice[r.filename] ?? ''}
                              disabled={busy || st?.state === 'done'}
                              onChange={e => setChoice(c => ({ ...c, [r.filename]: e.target.value }))}
                            >
                              <option value="">Skip this log</option>
                              {r.candidates.map(c => (
                                <option key={c.session_id} value={c.session_id}>
                                  {candidateLabel(c)}
                                  {c.has_ecu_data ? ' (already has ECU data)' : ''}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className={styles.noMatch}>
                              {r.error || 'No session of a matching length'}
                            </span>
                          )}
                        </td>
                        <td className={styles.tdMono}>
                          <span className={styles.verdict} style={{ color: v.color }}>{v.label}</span>
                          {top && (
                            <span className={styles.conf} style={{ color: confColor(top.confidence) }}>
                              {Math.round(top.confidence * 100)}%
                            </span>
                          )}
                        </td>
                        <td className={styles.tdActions}>
                          {st?.state === 'done' && <span className={styles.ok}>✓ {st.msg}</span>}
                          {st?.state === 'working' && <span className={styles.working}>merging…</span>}
                          {st?.state === 'error' && <span className={styles.bad} title={st.msg}>failed</span>}
                          {!st && choice[r.filename] && (
                            <button className={styles.linkBtn} onClick={() => reviewRow(r)} disabled={busy}>
                              Review…
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className={styles.tableHint}>
              <strong>Match</strong> is how strongly the log&apos;s shared channels
              line up with the session&apos;s once time-aligned. Anything below
              &ldquo;Confident&rdquo; is worth a look under Review before merging.
            </p>

            <div className={styles.footer}>
              <button className={styles.secondaryBtn} onClick={() => setStep(1)} disabled={busy}>
                Back
              </button>
              <button className={styles.primaryBtn} onClick={runMerges}
                      disabled={busy || !pending.length}>
                {busy ? 'Merging…' : `Merge ${pending.length} log${pending.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
